import React from "react";
import { ArrowUpRight, Github } from "lucide-react";
import { projects, personalInfo } from "../data/mock";
import { useReveal } from "../hooks/useReveal";

const SectionHeading = ({ num, title }) => (
  <div className="flex items-center gap-4 mb-12 md:mb-16">
    <h2 className="flex items-baseline gap-3 whitespace-nowrap">
      <span className="font-mono text-accent text-lg md:text-xl">{num}.</span>
      <span className="font-sans text-2xl md:text-3xl font-semibold text-ink tracking-tight">
        {title}
      </span>
    </h2>
    <span className="hidden sm:block h-px flex-1 bg-rule" />
  </div>
);

const ProjectCard = ({ project, index }) => {
  const flip = index % 2 === 1;
  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}>
      {/* Image */}
      <a
        href={project.github}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative block rounded-lg overflow-hidden border border-rule bg-bg-elev card-lift"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={project.image}
            alt={project.title}
            loading="lazy"
            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500 group-hover:scale-[1.02] transform duration-500"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-bg/40 to-transparent pointer-events-none" />
      </a>

      {/* Content */}
      <div>
        <p className="font-mono text-xs text-accent mb-3">{project.type || "Featured project"}</p>
        <h3 className="font-sans text-2xl md:text-3xl font-semibold text-ink mb-4 tracking-tight">
          <a
            href={project.github}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-accent transition-colors inline-flex items-center gap-2 group"
          >
            {project.title}
            <ArrowUpRight className="w-5 h-5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
          </a>
        </h3>
        <p className="text-[15px] leading-relaxed text-ink-2 mb-6 max-w-xl">
          {project.description}
        </p>
        <div className="flex flex-wrap gap-1.5 mb-6">
          {project.technologies.map((t) => (
            <span
              key={t}
              className="font-mono text-[11px] px-2.5 py-1 rounded-md border border-rule text-ink-2 hover:border-accent/40 hover:text-accent transition-colors"
            >
              {t}
            </span>
          ))}
        </div>
        <a
          href={project.github}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 font-mono text-sm text-ink-2 hover:text-accent transition-colors"
        >
          <Github className="w-4 h-4" strokeWidth={1.5} />
          view on github
        </a>
      </div>
    </div>
  );
};

const Projects = () => {
  const revealRef = useReveal();

  return (
    <div className="py-24 md:py-32 border-t border-rule">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div ref={revealRef} className="reveal">
          <SectionHeading num="04" title="Selected work" />

          <div className="space-y-24 md:space-y-28">
            {projects.map((p, i) => (
              <ProjectCard key={p.id} project={p} index={i} />
            ))}
          </div>

          <div className="mt-20 text-center">
            <a
              href={personalInfo.github}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 border border-rule text-ink-2 hover:text-accent hover:border-accent/40 rounded-md transition-all font-mono text-sm"
            >
              view archive on github
              <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Projects;
